import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCoursesByCategory } from "@/api/academies";

export default function AcademyCategoryCoursesPage() {
  const { academyId, categoryId } = useParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("جميع المستويات");
  const [selectedSort, setSelectedSort] = useState("الأحدث");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await fetchCoursesByCategory(academyId, categoryId);
        setCourses(data || []);
      } catch (err) {
        setError("حدث خطأ أثناء تحميل الكورسات");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [academyId, categoryId]);

  // تصفية الكورسات حسب المستوى
  const filteredCourses = selectedLevel === "جميع المستويات" 
    ? courses 
    : courses.filter(course => course.level === selectedLevel);

  // ترتيب الكورسات
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (selectedSort === "الأحدث") {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    } else if (selectedSort === "الأعلى سعراً") {
      return (b.price || 0) - (a.price || 0);
    } else if (selectedSort === "الأقل سعراً") {
      return (a.price || 0) - (b.price || 0);
    } else if (selectedSort === "الأعلى تقييماً") {
      return (b.rating || 0) - (a.rating || 0);
    }
    return 0;
  });

  // الحصول على مستويات فريدة من الكورسات
  const uniqueLevels = ["جميع المستويات", ...new Set(courses.map(course => course.level).filter(Boolean))];

  // تنسيقات CSS
  const styles = {
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "25px 20px",
      minHeight: "calc(100vh - 160px)"
    },
    header: {
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e9ecef"
    },
    title: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "32px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    subtitle: {
      margin: "0 0 20px 0",
      color: "#6c757d",
      fontSize: "18px"
    },
    filtersContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "30px",
      flexWrap: "wrap",
      gap: "15px",
      padding: "20px",
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.05)"
    },
    filterGroup: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
      flexWrap: "wrap"
    },
    filterLabel: {
      color: "#495057",
      fontWeight: "600",
      fontSize: "15px",
      minWidth: "80px"
    },
    select: {
      padding: "12px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "8px",
      fontSize: "15px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      cursor: "pointer",
      minWidth: "160px",
      transition: "all 0.3s",
      fontFamily: "inherit"
    },
    courseCount: {
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      padding: "8px 16px",
      borderRadius: "20px"
    },
    coursesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      gap: "25px",
      marginBottom: "40px"
    },
    courseCard: {
      backgroundColor: "white",
      borderRadius: "15px",
      overflow: "hidden",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s ease",
      position: "relative",
      height: "100%",
      display: "flex",
      flexDirection: "column"
    },
    courseImage: {
      width: "100%",
      height: "180px",
      backgroundColor: "#f8f9fa",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      position: "relative"
    },
    imagePlaceholder: {
      fontSize: "48px",
      color: "#4b6cb7",
      opacity: "0.5"
    },
    courseBadge: {
      position: "absolute",
      top: "15px",
      left: "15px",
      backgroundColor: "#4b6cb7",
      color: "white",
      padding: "5px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
      zIndex: "2"
    },
    courseContent: {
      padding: "20px",
      flex: "1",
      display: "flex",
      flexDirection: "column"
    },
    courseTitle: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "18px",
      fontWeight: "700",
      lineHeight: "1.4"
    },
    courseDescription: {
      margin: "0 0 15px 0",
      color: "#6c757d",
      fontSize: "14px",
      lineHeight: "1.5",
      flex: "1"
    },
    courseMeta: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "15px",
      paddingBottom: "15px",
      borderBottom: "1px solid #e9ecef"
    },
    levelBadge: {
      backgroundColor: "rgba(40, 167, 69, 0.1)",
      color: "#28a745",
      padding: "5px 12px",
      borderRadius: "15px",
      fontSize: "13px",
      fontWeight: "600"
    },
    ratingContainer: {
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    rating: {
      color: "#ffc107",
      fontWeight: "600",
      fontSize: "14px"
    },
    courseFooter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "auto"
    },
    price: {
      color: "#182848",
      fontSize: "22px",
      fontWeight: "700"
    },
    currency: {
      fontSize: "14px",
      color: "#6c757d",
      marginRight: "3px"
    },
    enrollButton: {
      padding: "10px 20px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      textDecoration: "none",
      display: "inline-block",
      textAlign: "center"
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      gap: "20px"
    },
    spinner: {
      width: "50px",
      height: "50px",
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #4b6cb7",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    },
    loadingText: {
      color: "#6c757d",
      fontSize: "18px",
      margin: "0"
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)"
    },
    emptyIcon: {
      fontSize: "64px",
      color: "#adb5bd",
      marginBottom: "20px",
      opacity: "0.5"
    },
    emptyTitle: {
      margin: "0 0 10px 0",
      color: "#495057",
      fontSize: "24px",
      fontWeight: "600"
    },
    emptyText: {
      margin: "0 0 25px 0",
      color: "#6c757d",
      fontSize: "16px",
      maxWidth: "400px",
      marginLeft: "auto",
      marginRight: "auto"
    },
    errorContainer: {
      textAlign: "center",
      padding: "60px 20px",
      backgroundColor: "#f8d7da",
      borderRadius: "15px",
      border: "1px solid #f5c6cb"
    },
    errorIcon: {
      fontSize: "48px",
      color: "#721c24",
      marginBottom: "20px"
    },
    errorTitle: {
      margin: "0 0 10px 0",
      color: "#721c24",
      fontSize: "24px",
      fontWeight: "600"
    },
    errorText: {
      margin: "0 0 25px 0",
      color: "#721c24",
      fontSize: "16px"
    },
    retryButton: {
      padding: "12px 25px",
      backgroundColor: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s"
    },
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      color: "#4b6cb7",
      textDecoration: "none",
      fontSize: "15px",
      fontWeight: "500",
      marginBottom: "15px",
      transition: "color 0.2s"
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      marginTop: "30px"
    },
    pageButton: {
      padding: "8px 15px",
      backgroundColor: "#f8f9fa",
      border: "1px solid #e1e5eb",
      borderRadius: "6px",
      color: "#495057",
      cursor: "pointer",
      transition: "all 0.3s",
      fontSize: "14px"
    },
    activePage: {
      backgroundColor: "#4b6cb7",
      color: "white",
      borderColor: "#4b6cb7"
    }
  };

  // أيقونات SVG
  const CourseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "32px", height: "32px", color: "#4b6cb7" }}>
      <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912.75.75 0 01-.584 0A49.87 49.87 0 002 9.268a.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
      <path d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.71 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67v-.645a48.549 48.549 0 013.44 1.668 2.25 2.25 0 002.12 0z" />
    </svg>
  );

  const StarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "16px", height: "16px", color: "#ffc107" }}>
      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
    </svg>
  );

  const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "16px", height: "16px" }}>
      <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clipRule="evenodd" />
    </svg>
  );

  // تأثيرات hover
  const handleCardHover = (e) => {
    e.currentTarget.style.transform = "translateY(-8px)";
    e.currentTarget.style.boxShadow = "0 15px 30px rgba(0, 0, 0, 0.15)";
  };

  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 5px 15px rgba(0, 0, 0, 0.08)";
  };

  const handleButtonHover = (e, isRetry = false) => {
    if (isRetry) {
      e.currentTarget.style.backgroundColor = "#c82333";
      e.currentTarget.style.transform = "translateY(-2px)";
    } else {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 7px 14px rgba(75, 108, 183, 0.2)";
    }
  };

  const handleButtonLeave = (e, isRetry = false) => {
    if (isRetry) {
      e.currentTarget.style.backgroundColor = "#dc3545";
      e.currentTarget.style.transform = "translateY(0)";
    } else {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }
  };

  // حالات التحميل والخطأ
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>جارٍ تحميل الكورسات...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>⚠️</div>
          <h3 style={styles.errorTitle}>حدث خطأ</h3>
          <p style={styles.errorText}>{error}</p>
          <button 
            style={styles.retryButton}
            onClick={() => window.location.reload()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonLeave(e, true)}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  if (sortedCourses.length === 0) {
    return (
      <div style={styles.container}>
        <a href="#" style={styles.backButton} onClick={() => window.history.back()}>
          <ArrowLeftIcon />
          العودة للخلف
        </a>
        
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📚</div>
          <h3 style={styles.emptyTitle}>لا توجد كورسات</h3>
          <p style={styles.emptyText}>
            {courses.length === 0 
              ? "لا توجد كورسات متاحة في هذه الفئة حالياً." 
              : `لا توجد كورسات تتطابق مع الفلتر: ${selectedLevel}`}
          </p>
          {selectedLevel !== "جميع المستويات" && (
            <button 
              style={styles.enrollButton}
              onClick={() => setSelectedLevel("جميع المستويات")}
              onMouseEnter={(e) => handleButtonHover(e)}
              onMouseLeave={(e) => handleButtonLeave(e)}
            >
              عرض جميع الكورسات
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* زر العودة */}
      <a href="#" style={styles.backButton} onClick={() => window.history.back()}>
        <ArrowLeftIcon />
        العودة للخلف
      </a>
      
      {/* رأس الصفحة */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <CourseIcon />
          كورسات الفئة
        </h1>
        <p style={styles.subtitle}>
          اكتشف مجموعة الكورسات المتاحة في هذه الفئة التعليمية
        </p>
      </div>

      {/* الفلاتر */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>فلترة:</span>
          <select 
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            style={styles.select}
          >
            {uniqueLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>ترتيب:</span>
          <select 
            value={selectedSort}
            onChange={(e) => setSelectedSort(e.target.value)}
            style={styles.select}
          >
            <option value="الأحدث">الأحدث</option>
            <option value="الأعلى سعراً">الأعلى سعراً</option>
            <option value="الأقل سعراً">الأقل سعراً</option>
            <option value="الأعلى تقييماً">الأعلى تقييماً</option>
          </select>
        </div>

        <div style={styles.courseCount}>
          {sortedCourses.length} كورس{sortedCourses.length !== 1 ? 'ات' : ''}
        </div>
      </div>

      {/* شبكة الكورسات */}
      <div style={styles.coursesGrid}>
        {sortedCourses.map((course, index) => (
          <div 
            key={course.id} 
            style={styles.courseCard}
            onMouseEnter={handleCardHover}
            onMouseLeave={handleCardLeave}
          >
            {/* شارة الكورس */}
            {course.is_popular && (
              <div style={styles.courseBadge}>الأكثر طلباً</div>
            )}
            
            {/* صورة الكورس */}
            <div style={styles.courseImage}>
              <div style={styles.imagePlaceholder}>📚</div>
            </div>
            
            {/* محتوى الكورس */}
            <div style={styles.courseContent}>
              <h3 style={styles.courseTitle}>{course.title}</h3>
              
              <p style={styles.courseDescription}>
                {course.description || "كورس تعليمي متخصص يغطي جميع جوانب الموضوع..."}
              </p>
              
              <div style={styles.courseMeta}>
                <span style={styles.levelBadge}>{course.level || "متوسط"}</span>
                
                {course.rating && (
                  <div style={styles.ratingContainer}>
                    <StarIcon />
                    <span style={styles.rating}>{course.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              
              <div style={styles.courseFooter}>
                <div>
                  <span style={styles.currency}>$</span>
                  <span style={styles.price}>{course.price || "0"}</span>
                </div>
                
                <Link 
                  to={`/course/${course.id}`}
                  style={styles.enrollButton}
                  onMouseEnter={(e) => handleButtonHover(e)}
                  onMouseLeave={(e) => handleButtonLeave(e)}
                >
                  التسجيل الآن
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* إضافة الأنيميشن */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .course-card {
          animation: fadeInUp 0.4s ease-out;
          animation-fill-mode: both;
        }
        
        .course-card:nth-child(1) { animation-delay: 0.1s; }
        .course-card:nth-child(2) { animation-delay: 0.2s; }
        .course-card:nth-child(3) { animation-delay: 0.3s; }
        .course-card:nth-child(4) { animation-delay: 0.4s; }
        .course-card:nth-child(5) { animation-delay: 0.5s; }
        .course-card:nth-child(6) { animation-delay: 0.6s; }
        
        @media (max-width: 768px) {
          .courses-grid {
            grid-template-columns: 1fr !important;
          }
          
          .filters-container {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .filter-group {
            width: 100% !important;
          }
          
          .select {
            width: 100% !important;
          }
          
          .course-count {
            align-self: center !important;
          }
        }
      `}</style>
    </div>
  );
}